"use client";

import * as Headless from "@headlessui/react";
import NextLink from "next/link";
import { useSelectedLayoutSegment } from "next/navigation";
import type React from "react";
import { type ComponentProps, forwardRef } from "react";
import { Link as IntlLink } from "@/i18n/navigation";

export const Link = forwardRef(function Link(
  {
    href,
    locale,
    ...rest
  }: Omit<ComponentProps<typeof IntlLink>, "locale"> & {
    locale?: string | boolean;
  },
  ref: React.ForwardedRef<HTMLAnchorElement>,
) {
  const isLogoutLink = href === "/auth/signout";
  const selectedLayoutSegment = useSelectedLayoutSegment();
  const pathname = selectedLayoutSegment ? `/${selectedLayoutSegment}` : "/";
  const isActive = pathname === href;

  if (isLogoutLink) {
    return (
      <Headless.DataInteractive>
        <a
          {...rest}
          aria-current={isActive ? "page" : undefined}
          data-active={isActive ? "" : undefined}
          href={href}
          ref={ref}
        />
      </Headless.DataInteractive>
    );
  }

  return (
    <Headless.DataInteractive>
      {typeof locale === "boolean" && !locale ? (
        <NextLink
          {...rest}
          aria-current={isActive ? "page" : undefined}
          data-active={isActive ? "" : undefined}
          href={href}
          ref={ref}
        />
      ) : (
        <IntlLink
          locale={locale as ComponentProps<typeof IntlLink>["locale"]}
          {...rest}
          aria-current={isActive ? "page" : undefined}
          data-active={isActive ? "" : undefined}
          href={href}
          ref={ref}
        />
      )}
    </Headless.DataInteractive>
  );
});
